import Alpine from 'alpinejs';
import { toDisplayTime } from '../../../Utils/toDisplayTime.js';
import { toMinutes } from '../../../Utils/toMinutes.js';
import { getLocalizedWeekday } from '../../../Utils/getLocalizedWeekday.js';

export function computeNextOpening(now, schedule, timezone, language, labelOpens) {
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const fmt = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, weekday: 'long' });
    const todayKey = fmt.format(now).toLowerCase();
    const startIdx = dayOrder.indexOf(todayKey);
    const scan = dayOrder.slice(startIdx).concat(dayOrder.slice(0, startIdx));

    for (let i = 0; i < scan.length; i++) {
        const key = scan[i];
        const slot = schedule[key];
        if (!slot || slot.length !== 2 || !slot[0] || !slot[1]) {
            continue;
        }

        if (i === 0) {
            const parts = new Intl.DateTimeFormat('en-GB', {
                timeZone: timezone,
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
            })
                .formatToParts(now)
                .reduce((acc, p) => {
                    acc[p.type] = p.value;
                    return acc;
                }, {});
            const current = `${parts.hour}:${parts.minute}`;
            const currentMinutes = toMinutes(current);
            const openMinutes = toMinutes(slot[0]);
            if (Number.isFinite(currentMinutes) && Number.isFinite(openMinutes) && currentMinutes < openMinutes) {
                return `${labelOpens} ${toDisplayTime(slot[0])}`;
            }
        } else {
            const future = new Date(now);
            future.setDate(now.getDate() + i);
            const dayName = getLocalizedWeekday(future, language, timezone);
            return `${labelOpens} ${dayName} ${toDisplayTime(slot[0])}`;
        }
    }
    return '';
}

export function computeStatus(now, timezone, language, labelOpens, schedule) {
    try {
        const formatter = new Intl.DateTimeFormat('en-GB', {
            timeZone: timezone,
            hour12: false,
            weekday: 'long',
            hour: '2-digit',
            minute: '2-digit',
        });
        const parts = formatter.formatToParts(now).reduce((acc, p) => {
            acc[p.type] = p.value;
            return acc;
        }, {});
        const weekday = (parts.weekday || '').toLowerCase();
        const currentTime = `${parts.hour || '00'}:${parts.minute || '00'}`;

        const todays = schedule[weekday];
        if (!todays || todays.length !== 2 || !todays[0] || !todays[1]) {
            return {
                isOpen: false,
                isClosingSoon: false,
                closingTime: null,
                nextOpeningText: computeNextOpening(now, schedule, timezone, language, labelOpens),
            };
        }

        const [openStr, closeStr] = todays;
        const nowMinutes = toMinutes(currentTime);
        const openMinutes = toMinutes(openStr);
        const closeMinutes = toMinutes(closeStr);
        const isOpen =
            Number.isFinite(nowMinutes) &&
            Number.isFinite(openMinutes) &&
            Number.isFinite(closeMinutes) &&
            nowMinutes >= openMinutes &&
            nowMinutes < closeMinutes;
        const diff = closeMinutes - nowMinutes;
        const isClosingSoon = isOpen && diff >= 0 && diff <= 60;

        return {
            isOpen,
            isClosingSoon,
            closingTime: closeStr,
            nextOpeningText: isOpen ? '' : computeNextOpening(now, schedule, timezone, language, labelOpens),
        };
    } catch (e) {
        return {
            isOpen: false,
            isClosingSoon: false,
            closingTime: null,
            nextOpeningText: 'Error loading schedule',
        };
    }
}

Alpine.data('locationOpeningStatus', (labelOpens, language = 'de', schedule = {}) => ({
    intervalId: null,

    // computed properties
    isOpen: false,
    isClosingSoon: false,
    displayClosingTime: null,
    nextOpeningText: '',

    init() {
        this.compute();
        this.intervalId = window.setInterval(() => this.compute(), 6000);
    },

    compute() {
        try {
            const now = new Date();
            const result = computeStatus(now, 'Europe/Vienna', language, labelOpens, schedule);
            this.isOpen = result.isOpen;
            this.isClosingSoon = result.isClosingSoon;
            this.displayClosingTime = toDisplayTime(result.closingTime);
            this.nextOpeningText = result.nextOpeningText;
        } catch (e) {
            console.error('LocationOpeningStatus compute error', e);
            this.isOpen = false;
            this.isClosingSoon = false;
            this.nextOpeningText = '';
        }
    },
}));
